// components/SkopaFilters.tsx
import { Toggle } from "@/client/components/shad/toggle";
import { IconCircle, IconLoader2 } from "@tabler/icons-react";
import { useAircraftFilters } from "./hooks/useAircraftFilters";
import {
  columnIds,
  rowIds,
} from "@/client/map/managers/aircraft/AircraftFilterEngine";
import type {
  ColumnId,
  RowId,
} from "@/client/map/managers/aircraft/AircraftFilterEngine";
import { Skeleton } from "@/client/components/shad/skeleton";

export type ToggleCell = {
  id: string;
  label?: string;
  icon?: boolean;
  className?: string;
  variant?: "default" | "outline";
};

// Baked directly into the file layout to ensure it's never undefined on mount
const toggleGrid: ToggleCell[][] = [
  [
    {
      id: "all",
      label: "ALL",
      variant: "default",
      className:
        "text-white-300 text-xs data-[state=on]:bg-sidebar-accent-foreground/20 dark:data-[state=on]:hover:bg-sidebar-accent-foreground/10",
    },
    {
      id: "a",
      label: "A",
      className:
        "text-white-300 text-xs data-[state=on]:bg-sidebar-accent-foreground/10 dark:data-[state=on]:hover:bg-sidebar-accent-foreground/10",
    },
    {
      id: "su",
      label: "SU",
      className:
        "text-white-300 text-xs data-[state=on]:bg-sidebar-accent-foreground/10 dark:data-[state=on]:hover:bg-sidebar-accent-foreground/10",
    },
    {
      id: "ss",
      label: "SS",
      className:
        "text-white-300 text-xs data-[state=on]:bg-sidebar-accent-foreground/10 dark:data-[state=on]:hover:bg-sidebar-accent-foreground/10",
    },
    {
      id: "sp",
      label: "SP",
      className:
        "text-white-300 text-xs data-[state=on]:bg-sidebar-accent-foreground/10 dark:data-[state=on]:hover:bg-sidebar-accent-foreground/10",
    },
    {
      id: "l",
      label: "L",
      className:
        "text-white-300 text-xs data-[state=on]:bg-sidebar-accent-foreground/10 dark:data-[state=on]:hover:bg-sidebar-accent-foreground/10",
    },
  ],
  [
    {
      id: "p",
      label: "P",
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "p-a",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "p-su",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "p-ss",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "p-sp",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "p-l",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
  ],
  [
    {
      id: "u",
      label: "U",
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "u-a",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "u-su",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "u-ss",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "u-sp",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "u-l",
      icon: true,
      className:
        "text-amber-300 text-xs data-[state=on]:border-amber-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
  ],
  [
    {
      id: "f",
      label: "F",
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "f-a",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "f-su",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "f-ss",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "f-sp",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "f-l",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
  ],
  [
    {
      id: "future",
      label: "?F",
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "future-a",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "future-su",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "future-ss",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "future-sp",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "future-l",
      icon: true,
      className:
        "text-sky-300 text-xs data-[state=on]:border-sky-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
  ],
  [
    {
      id: "n",
      label: "N",
      className:
        "text-lime-300 text-xs data-[state=on]:border-lime-300 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "n-a",
      icon: true,
      className:
        "text-lime-300 text-xs data-[state=on]:border-lime-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "n-su",
      icon: true,
      className:
        "text-lime-300 text-xs data-[state=on]:border-lime-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "n-ss",
      icon: true,
      className:
        "text-lime-300 text-xs data-[state=on]:border-lime-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "n-sp",
      icon: true,
      className:
        "text-lime-300 text-xs data-[state=on]:border-lime-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "n-l",
      icon: true,
      className:
        "text-lime-300 text-xs data-[state=on]:border-lime-300/50 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
  ],
  [
    {
      id: "s",
      label: "S",
      className:
        "text-red-300/80 text-xs data-[state=on]:border-red-300/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "s-a",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "s-su",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "s-ss",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "s-sp",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "s-l",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
  ],
  [
    {
      id: "h",
      label: "H",
      className:
        "text-red-300/80 text-xs data-[state=on]:border-red-300/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "h-a",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "h-su",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "h-ss",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "h-sp",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
    {
      id: "h-l",
      icon: true,
      className:
        "text-red-400 text-xs data-[state=on]:border-red-400/80 dark:data-[state=on]:hover:bg-sidebar-accent",
    },
  ],
];

const COL_SET = new Set<string>(columnIds);
const ROW_SET = new Set<string>(rowIds);

export const SkopaFilters = () => {
  const { snapshot, toggleAll, toggleRow, toggleCol, toggleCell } =
    useAircraftFilters();

  if (!snapshot) {
    return <Skeleton className="w-full h-72" />;
  }

  return (
    <div className="grid gap-1.5 grid-cols-6 grid-rows-8 saturate-50 brightness-90 hover:saturate-100 hover:brightness-100 duration-400">
      {toggleGrid.flat().map((cell) => {
        let isActive = false;
        let toggleAction: () => void;

        if (cell.id === "all") {
          isActive = snapshot.all;
          toggleAction = () => toggleAll();
        } else if (COL_SET.has(cell.id)) {
          isActive = snapshot.cols[cell.id as ColumnId] ?? false;
          toggleAction = () => toggleCol(cell.id as ColumnId);
        } else if (ROW_SET.has(cell.id)) {
          isActive = snapshot.rows[cell.id as RowId] ?? false;
          toggleAction = () => toggleRow(cell.id as RowId);
        } else {
          isActive = snapshot.cells[cell.id] ?? false;
          const [row, col] = cell.id.split("-");
          toggleAction = () => toggleCell(row as RowId, col as ColumnId);
        }

        return (
          <Toggle
            key={cell.id}
            size="sm"
            pressed={isActive}
            onPressedChange={toggleAction}
            variant={cell.variant ?? "outline"}
            className={`${cell.className} size-7.5`}
            aria-label={cell.label ?? cell.id}
          >
            {cell.icon ? <IconCircle className="size-2" /> : cell.label}
          </Toggle>
        );
      })}
    </div>
  );
};
